import { useEffect, useRef, useState } from 'react';
import {
  collection, doc, onSnapshot, addDoc, deleteDoc, setDoc, serverTimestamp, getDocs,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(ts) {
  if (!ts?.seconds) return '';
  return new Date(ts.seconds * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

// requests/{requestId}/socialMediaPlan/{docId} — file docs use auto IDs;
// the freeform "what to capture" brief, the workflow status and the Padelante
// game content checklist each live in a single reserved doc id.
const NOTES_DOC_ID = 'notes';
const STATUS_DOC_ID = 'status';
const CHECKLIST_DOC_ID = 'gameContent';
const RESERVED_DOC_IDS = [NOTES_DOC_ID, STATUS_DOC_ID, CHECKLIST_DOC_ID];

const STATUS_META = {
  draft:             { label: 'Not sent to the tour leader yet', color: '#6B7280', bg: '#F3F4F6' },
  ready_for_review:  { label: 'Awaiting tour leader review',     color: '#92400E', bg: '#FFF7ED' },
  acknowledged:      { label: 'Reviewed — awaiting content',     color: '#1A4A8A', bg: '#EFF6FF' },
  content_uploaded:  { label: 'Content uploaded — awaiting acknowledgement', color: '#9D174D', bg: '#FCE7F3' },
  closed:            { label: 'Complete',                        color: '#065F46', bg: '#ECFDF5' },
};

// role: 'marketing' | 'staff' | 'tour_leader' — staff can act on either side.
export default function SocialMediaPlan({ requestId, role = 'tour_leader' }) {
  const { currentUser } = useAuth();
  const [files, setFiles] = useState([]);
  const [notes, setNotes] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [savingNotes, setSavingNotes] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef();

  const canEdit = role === 'marketing' || role === 'staff';
  const isMarketingSide = role === 'marketing' || role === 'staff';
  const isLeaderSide = role === 'tour_leader' || role === 'staff';
  const statusValue = status?.value || 'draft';

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'requests', requestId, 'socialMediaPlan'), (snap) => {
      const fileDocs = [];
      let notesText = '';
      let statusData = null;
      snap.docs.forEach((d) => {
        if (d.id === NOTES_DOC_ID) {
          notesText = d.data().text || '';
        } else if (d.id === STATUS_DOC_ID) {
          statusData = d.data();
        } else if (!RESERVED_DOC_IDS.includes(d.id)) {
          fileDocs.push({ id: d.id, ...d.data() });
        }
      });
      fileDocs.sort((a, b) => (a.createdAt?.seconds ?? 0) - (b.createdAt?.seconds ?? 0));
      setFiles(fileDocs);
      setNotes(notesText);
      setNotesDraft(notesText);
      setStatus(statusData);
      setLoading(false);
    });
    return unsub;
  }, [requestId]);

  async function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      setError('File must be under 20 MB.');
      return;
    }
    setError('');
    setUploading(true);
    setProgress(0);

    const storagePath = `socialMediaPlans/${requestId}/${Date.now()}_${file.name}`;
    const fileRef = storageRef(storage, storagePath);
    const uploadTask = uploadBytesResumable(fileRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => setProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)),
      (err) => {
        console.error(err);
        setError('Upload failed. Please try again.');
        setUploading(false);
      },
      async () => {
        const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
        await addDoc(collection(db, 'requests', requestId, 'socialMediaPlan'), {
          name: file.name,
          storagePath,
          downloadUrl,
          mimeType: file.type,
          size: file.size,
          uploadedByEmail: currentUser.email,
          createdAt: serverTimestamp(),
        });
        setUploading(false);
        setProgress(0);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    );
  }

  async function handleDelete(file) {
    if (!window.confirm(`Delete "${file.name}"?`)) return;
    try {
      await deleteObject(storageRef(storage, file.storagePath));
    } catch (err) {
      console.warn('Storage delete failed:', err);
    }
    await deleteDoc(doc(db, 'requests', requestId, 'socialMediaPlan', file.id));
  }

  async function handleSaveNotes() {
    setSavingNotes(true);
    try {
      await setDoc(doc(db, 'requests', requestId, 'socialMediaPlan', NOTES_DOC_ID), {
        text: notesDraft,
        updatedByEmail: currentUser.email,
        updatedAt: serverTimestamp(),
      });
      setNotes(notesDraft);
    } finally {
      setSavingNotes(false);
    }
  }

  async function handleDeletePlan() {
    if (!window.confirm('Delete this entire social media plan? This will remove all notes, files, and status. This cannot be undone.')) return;
    try {
      const snap = await getDocs(collection(db, 'requests', requestId, 'socialMediaPlan'));
      await Promise.all(snap.docs.map(async (d) => {
        const data = d.data();
        if (data.storagePath) {
          try { await deleteObject(storageRef(storage, data.storagePath)); } catch {}
        }
        await deleteDoc(doc(db, 'requests', requestId, 'socialMediaPlan', d.id));
      }));
    } catch (err) {
      console.error('Delete plan failed:', err);
      setError('Failed to delete plan. Please try again.');
    }
  }

  async function setStatusValue(value, extra = {}) {
    setStatusSaving(true);
    try {
      await setDoc(doc(db, 'requests', requestId, 'socialMediaPlan', STATUS_DOC_ID), { value, ...extra }, { merge: true });
    } finally {
      setStatusSaving(false);
    }
  }

  const hasContent = notes.trim().length > 0 || files.length > 0;

  if (loading) {
    return <p className="text-[13px] text-[#9CA3AF]">Loading…</p>;
  }

  const meta = STATUS_META[statusValue] || STATUS_META.draft;

  return (
    <div className="space-y-4">
      {/* Status tracker */}
      <div className="rounded-[10px] px-3 py-2.5 flex items-center justify-between gap-3 flex-wrap" style={{ backgroundColor: meta.bg }}>
        <p className="text-[12.5px] font-semibold" style={{ color: meta.color }}>{meta.label}</p>
        <div className="flex items-center gap-2 flex-wrap">
          {role === 'staff' && (
            <button
              onClick={handleDeletePlan}
              className="text-[12px] font-medium text-dr hover:text-red-800 border border-dr/20 hover:border-dr/40 px-3 py-1.5 rounded-[7px] transition"
            >
              Delete plan
            </button>
          )}
          {isMarketingSide && statusValue === 'draft' && (
            <button
              onClick={() => setStatusValue('ready_for_review', { readyForReviewAt: serverTimestamp() })}
              disabled={statusSaving || !hasContent}
              title={!hasContent ? 'Add notes or a file before confirming' : undefined}
              className="text-[12px] font-bold text-white bg-pb hover:bg-navy px-3 py-1.5 rounded-[7px] transition disabled:opacity-40"
            >
              Confirm plan & notify tour leader
            </button>
          )}
          {isLeaderSide && statusValue === 'ready_for_review' && (
            <button
              onClick={() => setStatusValue('acknowledged', { acknowledgedAt: serverTimestamp(), acknowledgedByEmail: currentUser.email })}
              disabled={statusSaving}
              className="text-[12px] font-bold text-white bg-pb hover:bg-navy px-3 py-1.5 rounded-[7px] transition disabled:opacity-40"
            >
              I've received &amp; understood this
            </button>
          )}
          {isLeaderSide && statusValue === 'acknowledged' && (
            <button
              onClick={() => setStatusValue('content_uploaded', { contentUploadedAt: serverTimestamp(), contentUploadedByEmail: currentUser.email })}
              disabled={statusSaving}
              className="text-[12px] font-bold text-white bg-pb hover:bg-navy px-3 py-1.5 rounded-[7px] transition disabled:opacity-40"
            >
              Content uploaded
            </button>
          )}
          {isMarketingSide && statusValue === 'content_uploaded' && (
            <button
              onClick={() => setStatusValue('closed', { closedAt: serverTimestamp() })}
              disabled={statusSaving}
              className="text-[12px] font-bold text-white bg-pb hover:bg-navy px-3 py-1.5 rounded-[7px] transition disabled:opacity-40"
            >
              Acknowledge
            </button>
          )}
        </div>
      </div>
      {statusValue === 'acknowledged' && status?.acknowledgedAt && (
        <p className="text-[11px] text-[#9CA3AF] -mt-2">Reviewed {formatDate(status.acknowledgedAt)} by {status.acknowledgedByEmail}</p>
      )}
      {(statusValue === 'content_uploaded' || statusValue === 'closed') && status?.contentUploadedAt && (
        <p className="text-[11px] text-[#9CA3AF] -mt-2">Content uploaded {formatDate(status.contentUploadedAt)} by {status.contentUploadedByEmail}</p>
      )}

      <div>
        <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-2">What to capture</p>
        {canEdit ? (
          <div className="space-y-2">
            <textarea
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              rows={4}
              placeholder="e.g. Capture arrival, training sessions, fixture highlights and the hotel — post daily to Instagram Stories…"
              className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
            />
            <button
              onClick={handleSaveNotes}
              disabled={savingNotes || notesDraft === notes}
              className="text-[12px] font-medium text-pb hover:text-navy border border-pb/20 hover:border-pb/40 px-3 py-1.5 rounded-[8px] transition disabled:opacity-50"
            >
              {savingNotes ? 'Saving…' : 'Save notes'}
            </button>
          </div>
        ) : (
          <p className="text-[13px] text-[#374151] whitespace-pre-wrap">
            {notes || 'No notes from marketing yet.'}
          </p>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Files</p>
          {canEdit && (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="text-[12px] font-medium text-pb hover:text-navy border border-pb/20 hover:border-pb/40 px-3 py-1.5 rounded-[8px] transition disabled:opacity-50"
            >
              + Upload file
            </button>
          )}
          {canEdit && (
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.txt"
            />
          )}
        </div>

        {uploading && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-[12px] text-[#6B7280] mb-1">
              <span>Uploading…</span>
              <span>{progress}%</span>
            </div>
            <div className="w-full bg-[#E5E7EB] rounded-full h-1.5">
              <div className="bg-pb h-1.5 rounded-full transition-all duration-200" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {error && <p className="text-[12px] text-dr mb-2">{error}</p>}

        {files.length === 0 ? (
          <p className="text-[13px] text-[#9CA3AF]">No files uploaded yet.</p>
        ) : (
          <div className="space-y-2">
            {files.map((f) => (
              <div key={f.id} className="flex items-center gap-3 p-3 rounded-[8px] bg-surface border border-[#F3F4F6] group">
                <div className="flex-1 min-w-0">
                  <a href={f.downloadUrl} target="_blank" rel="noreferrer" className="text-[13px] font-medium text-[#111827] hover:text-pb truncate block">
                    {f.name}
                  </a>
                  <p className="text-[12px] text-[#9CA3AF]">{formatBytes(f.size)} · {f.uploadedByEmail}</p>
                </div>
                {canEdit && (
                  <button
                    onClick={() => handleDelete(f)}
                    className="text-[12px] text-[#D1D5DB] hover:text-dr opacity-0 group-hover:opacity-100 transition flex-shrink-0"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
